import { useMemo } from 'react';
import Box from '@mui/material/Box';
import { styled, alpha } from '@mui/material/styles';
import { useQueryClient } from '@tanstack/react-query';
import { BottomNavItem } from './BottomNavItem';
import { customerBottomNavItems } from './navigationConfig';
import type { BottomNavItem as BottomNavItemType } from './navigationConfig';
import type { VendorBookingsResponse } from '../../features/bookings/hooks/useVendorBookings';

interface BottomNavigationProps {
  items?: BottomNavItemType[];
  onMorePress?: () => void;
}

const BottomNavContainer = styled(Box)(({ theme }) => ({
  position: 'fixed',
  bottom: 0,
  left: 0,
  right: 0,
  minHeight: 64,
  backgroundColor: alpha(theme.palette.background.paper, 0.98),
  backdropFilter: 'blur(10px)',
  WebkitBackdropFilter: 'blur(10px)',
  borderTop: `1px solid ${theme.palette.surface.glassBorder}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-around',
  padding: theme.spacing(1, 0),
  paddingBottom: `calc(${theme.spacing(1)} + env(safe-area-inset-bottom))`,
  zIndex: theme.zIndex.appBar + 1,

  [theme.breakpoints.up('md')]: {
    display: 'none',
  },
}));

/**
 * Read the count of today's confirmed (not yet checked-in) bookings
 * from the React Query cache. Returns 0 when the dashboard hasn't
 * been visited yet (no cached data).
 */
function useTodayBookingBadgeCount(): number {
  const queryClient = useQueryClient();

  return useMemo(() => {
    // Find any cached vendor bookings query (key starts with ['vendor', 'bookings'])
    const queries = queryClient.getQueriesData<VendorBookingsResponse>({
      queryKey: ['vendor', 'bookings'],
    });

    if (!queries.length) return 0;

    // Use the first matching cached result (typically today's bookings from dashboard)
    const [, data] = queries[0];
    if (!data?.data) return 0;

    // Count confirmed bookings that have not been checked in yet
    return data.data.filter((booking) => booking.status === 'CONFIRMED' && !booking.checkInTime)
      .length;
  }, [queryClient]);
}

export function BottomNavigation({ items, onMorePress }: BottomNavigationProps) {
  const navItems = items || customerBottomNavItems;
  const todayBadgeCount = useTodayBookingBadgeCount();

  return (
    <BottomNavContainer>
      {navItems.map((item) => (
        <BottomNavItem
          key={item.path}
          label={item.label}
          path={item.path}
          iconOutlined={item.iconOutlined}
          iconFilled={item.iconFilled}
          onPress={item.path === '__drawer__' ? onMorePress : undefined}
          badgeCount={item.path === '/vendor/bookings' ? todayBadgeCount : undefined}
        />
      ))}
    </BottomNavContainer>
  );
}
